import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useTheme } from '../../App';
import { PageLayout } from '../../components/Sidebar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Briefcase, Users, Settings } from 'lucide-react';
import JobsTab from './JobsTab';
import ApplicantsTab from './ApplicantsTab';
import StagesTab from './StagesTab';

const API = '/api';

export default function RecruitmentPage() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('jobs');
  const [refData, setRefData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => {
    const token = localStorage.getItem('orka_session_token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }, []);

  const fetchRefData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/recruitment/reference-data`, { headers: headers() });
      if (res.ok) setRefData(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/recruitment/jobs`, { headers: headers() });
      if (res.ok) setJobs(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchApplications = useCallback(async () => {
    try {
      const res = await fetch(`${API}/recruitment/applications`, { headers: headers() });
      if (res.ok) setApplications(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  const reload = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchRefData(), fetchJobs(), fetchApplications()]);
    setLoading(false);
  }, [fetchRefData, fetchJobs, fetchApplications]);

  useEffect(() => { reload(); }, [reload]);

  const apiCall = useCallback(async (method, path, body) => {
    const res = await fetch(`${API}${path}`, {
      method, headers: headers(),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Request failed');
    }
    return res.json();
  }, [headers]);

  if (!user) return null;

  return (
    <PageLayout title="Recruitment" user={user} onLogout={logout} theme={theme} setTheme={setTheme}>
      <div data-testid="recruitment-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="jobs" data-testid="tab-jobs" className="gap-1.5">
              <Briefcase className="w-3.5 h-3.5" /> Jobs
            </TabsTrigger>
            <TabsTrigger value="applicants" data-testid="tab-applicants" className="gap-1.5">
              <Users className="w-3.5 h-3.5" /> Applicants
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="gap-1.5">
              <Settings className="w-3.5 h-3.5" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="jobs">
            <JobsTab jobs={jobs} refData={refData} loading={loading} apiCall={apiCall} onRefresh={reload} />
          </TabsContent>
          <TabsContent value="applicants">
            <ApplicantsTab applications={applications} jobs={jobs} refData={refData} loading={loading} apiCall={apiCall} onRefresh={reload} />
          </TabsContent>
          <TabsContent value="settings">
            <StagesTab refData={refData} apiCall={apiCall} onRefresh={reload} />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
}
